import { useCallback, useContext, useMemo } from 'react';
import api from 'api';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from './useGetMixpanelProps';
import {
  DocumentEventProps,
  WitnessedDocumentViewedEventProps,
} from 'models/MixpanelEvents';
import { getIsOwner } from 'utils';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { getIsBuySide } from 'router/subrouters/DataRoom/utils';

type UseMixpanelDocumentEventsProps = {
  documentName: string;
  dataRoomName?: string;
  ownerId: string;
  userSide: QAndASides;
};

const useMixpanelDocumentEvents = ({
  documentName,
  dataRoomName,
  ownerId,
  userSide,
}: UseMixpanelDocumentEventsProps) => {
  const {
    currentUser: { _id: currentUserId },
  } = useContext(CurrentUserContext);

  const getMixpanelProps = useGetMixpanelProps();

  const isOwner = useMemo(
    () => getIsOwner(ownerId, currentUserId),
    [currentUserId, ownerId],
  );

  const onDocumentViewed = useCallback(
    (time: number) => {
      mixpanelService.documentViewed(
        getMixpanelProps({
          documentName,
          isOwner,
          userSide,
          dataRoomName,
          timeSpentInSeconds: time,
        }) as DocumentEventProps,
      );

      if (getIsBuySide(userSide)) {
        api.mixpanel.witnessedDocumentViewed(
          getMixpanelProps({
            userId: ownerId,
            triggererId: currentUserId,
            dataRoomName,
            documentName,
            isWitnessedEvent: true,
            timeSpentInSeconds: time,
          }) as WitnessedDocumentViewedEventProps,
        );
      }
    },
    [
      documentName,
      isOwner,
      userSide,
      dataRoomName,
      ownerId,
      currentUserId,
      getMixpanelProps,
    ],
  );

  return { onDocumentViewed };
};

export default useMixpanelDocumentEvents;
